/**
 * Toast notification context.
 *
 * Provides app-wide toast notifications.
 * Integrates with WebSocket for real-time alerts.
 */

import { createContext, useCallback, useContext, useState, ReactNode } from 'react'
import { ToastContainer, ToastData } from '../components/Alerts/Toast'
import { useWebSocket, LiveEvent } from '../hooks/useWebSocket'
import { useAuth } from './AuthContext'

interface ToastContextValue {
  /** Add a toast notification */
  addToast: (toast: Omit<ToastData, 'id'>) => void
  /** Remove a toast by ID */
  removeToast: (id: string) => void
  /** Clear all toasts */
  clearToasts: () => void
  /** WebSocket connection status */
  connectionStatus: 'connecting' | 'connected' | 'disconnected' | 'error'
}

const ToastContext = createContext<ToastContextValue | null>(null)

let toastIdCounter = 0

// Map alert types to toast types
function getToastType(alertType: string): ToastData['type'] {
  switch (alertType) {
    case 'stock_trade':
      return 'info'
    case 'bill_introduced':
      return 'alert'
    case 'vote_completed':
      return 'success'
    case 'prediction_market':
      return 'warning'
    default:
      return 'info'
  }
}

// Get icon for alert type
function getAlertIcon(type: string): string {
  switch (type) {
    case 'stock_trade':
      return '📈'
    case 'bill_introduced':
      return '📜'
    case 'vote_completed':
      return '🗳️'
    case 'prediction_market':
      return '📊'
    default:
      return '🔔'
  }
}

interface ToastProviderProps {
  children: ReactNode
  /** Enable real-time WebSocket alerts as toasts */
  enableLiveAlerts?: boolean
  /** Maximum number of toasts to show at once */
  maxToasts?: number
}

export function ToastProvider({
  children,
  enableLiveAlerts = true,
  maxToasts = 5
}: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastData[]>([])
  const { user } = useAuth()

  const addToast = useCallback((toast: Omit<ToastData, 'id'>) => {
    const id = `toast-${Date.now()}-${++toastIdCounter}`
    setToasts(prev => {
      const newToasts = [{ ...toast, id }, ...prev]
      return newToasts.slice(0, maxToasts)
    })
  }, [maxToasts])

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id))
  }, [])

  const clearToasts = useCallback(() => {
    setToasts([])
  }, [])

  // Handle WebSocket events
  const handleEvent = useCallback((event: LiveEvent) => {
    if (!enableLiveAlerts) return
    
    addToast({
      type: getToastType(event.type),
      title: event.title || 'New Update',
      message: event.message,
      icon: getAlertIcon(event.type),
      duration: 6000, // 6 seconds for live alerts
    })
  }, [enableLiveAlerts, addToast])

  const { status } = useWebSocket({
    onEvent: handleEvent,
    autoConnect: enableLiveAlerts,
    userId: user?.id,
  })

  return (
    <ToastContext.Provider value={{ 
      addToast, 
      removeToast, 
      clearToasts,
      connectionStatus: status,
    }}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={removeToast} />
    </ToastContext.Provider>
  )
}

export function useToast() {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}
